import { Delete, Link, VideoFile } from '@mui/icons-material';
import VisuallyHiddenInput from './VisuallyHiddenInput';
import { Button, Card, CardActions, CardContent, Snackbar, Tooltip, Typography, Box } from '@mui/material';
import React, { useState } from 'react';
import VideoJS from '../VideoJS/VideoJS';
import { copyToClipboard } from '../../utils/clipboard';

const isMuxVideo = (url) => {
  // Check if the URL is a MUX video ID (typically a short alphanumeric string)
  return /^[a-zA-Z0-9]{6,}$/.test(url);
};

const isVideoUrl = (url) => {
  // Check if the URL ends with common video extensions
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

/**
 * VideoContainerPreview component for displaying and managing video content
 * @param {Object} props
 * @param {string} props.title - Title of the video container
 * @param {string} props.video - Video URL or MUX playback ID
 * @param {string} props.name - Name identifier for the video
 * @param {Function} props.handleRemove - Callback when video is removed
 * @param {Function} props.handleVideoUpload - Callback when video is uploaded
 * @param {boolean} props.showCopyUrlButton - Whether to show the copy URL button
 * @param {Function} [props.onMetadataLoaded] - Optional callback when video metadata is loaded, receives (formattedDuration, duration)
 * @param {Object} props.sx - Additional styles for the container
 */
export default function VideoContainerPreview({
  title,
  video,
  name,
  handleRemove,
  handleVideoUpload,
  showCopyUrlButton,
  onMetadataLoaded = () => { },
  ...props
}) {
  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleCopyUrl = () => {
    if (video) {
      copyToClipboard(video);
      setSnackbarMessage('URL copied to clipboard!');
      setOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpen(false);
    setSnackbarMessage('');
  };

  const handleVideoMetadata = (event) => {
    if (onMetadataLoaded) {
      const duration = Math.round(event.target.duration);
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      onMetadataLoaded(formattedDuration, duration);
    }
  };

  const renderVideo = () => {
    if (!video) return null;

    if (isMuxVideo(video)) {
      return <VideoJS playbackID={video} />;
    }

    if (isVideoUrl(video)) {
      return (
        <video
          controls
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          src={video}
          onLoadedMetadata={handleVideoMetadata}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    // If it's neither a MUX video nor a direct video URL, show an error message
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="error" sx={{ padding: 4 }}>
          Invalid video format
        </Typography>
      </Box>
    );
  };
  return (
    <Card>
      {video ? (
        <Box {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...props.sx }}>
          {renderVideo()}
        </Box>
      ) : (
        <Box {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...props.sx }}>
          <Typography variant="body2" color="text.secondary">
            No video available.
          </Typography>
        </Box>
      )}
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
      <CardActions>
        {video ? (
          <>
            <Button size="small" color="error" startIcon={<Delete />} name={name} onClick={handleRemove}>
              Remove
            </Button>
            {showCopyUrlButton && (
              <Tooltip title={video} placement="bottom">
                <Button size="small" color="primary" startIcon={<Link />} onClick={handleCopyUrl}>
                  Copy URL
                </Button>
              </Tooltip>
            )}
          </>
        ) : (
          <Button
            fullWidth
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<VideoFile />}
          >
            Add Video
            <VisuallyHiddenInput type="file" name={name} accept="video/*" onChange={handleVideoUpload} />
          </Button>
        )}
      </CardActions>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
} 